class LuxuryHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-luxury-track]');
    this.stage = this.querySelector('[data-luxury-stage]');
    this.slides = Array.from(this.querySelectorAll('[data-luxury-slide]'));
    this.brand = this.querySelector('[data-luxury-hero-brand]');
    this.content = this.querySelector('[data-luxury-hero-content]');
    this.duration = Number(this.dataset.slideDuration || 4) * 1000;
    this.morphEnabled = this.dataset.morphEnabled === 'true';
    this.intervalId = null;
    this.rafId = null;
    this.morphDone = false;
    this.morphPlaying = false;
    this.lastY = window.scrollY || 0;
    this.touchStartY = null;
    this.morphEndTimer = null;

    this.header = document.querySelector('[data-luxury-header]');
    this.onScroll = this.onScroll.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.nextSlide = this.nextSlide.bind(this);
    this.onResize = this.onResize.bind(this);

    const activeSlides = this.slides.filter((slide) => slide.querySelector('img'));
    if (activeSlides.length > 1) {
      this.slides = activeSlides;
      this.currentIndex = 0;
      this.intervalId = window.setInterval(this.nextSlide, this.duration);
    }

    if (this.morphEnabled) {
      document.body.classList.add('has-brand-morph');
      this.hideContent();
    } else {
      this.showContent();
    }

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('wheel', this.onWheel, { passive: true });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: true });
    window.addEventListener('resize', this.onResize);

    document.body.classList.add('has-luxury-hero');

    this.resetBrand(true);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('resize', this.onResize);
    if (this.intervalId) window.clearInterval(this.intervalId);
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    if (this.morphEndTimer) window.clearTimeout(this.morphEndTimer);
  }

  nextSlide() {
    if (this.slides.length < 2) return;
    this.slides[this.currentIndex]?.classList.remove('is-active');
    this.currentIndex = (this.currentIndex + 1) % this.slides.length;
    this.slides[this.currentIndex]?.classList.add('is-active');
  }

  onResize() {
    if (this.morphDone || this.morphPlaying) return;
    this.resetBrand(true);
  }

  showContent() {
    this.classList.add('is-content-visible');
  }

  hideContent() {
    this.classList.remove('is-content-visible');
  }

  resetBrand(full = false) {
    if (!this.brand) return;
    if (this.morphEndTimer) {
      window.clearTimeout(this.morphEndTimer);
      this.morphEndTimer = null;
    }
    this.brand.classList.remove('is-morphed', 'is-morphing');
    this.brand.style.transition = 'none';
    this.brand.style.transform = '';
    this.brand.style.opacity = '';
    this.brand.style.visibility = '';
    if (full) {
      this.morphDone = false;
      this.morphPlaying = false;
      if (this.morphEnabled) this.hideContent();
      if (this.header?.setBrandArrived) this.header.setBrandArrived(false);
      else this.header?.classList.remove('is-brand-arrived');
    }
  }

  canTriggerMorph() {
    if (!this.brand || !this.morphEnabled) return false;
    if (this.morphDone || this.morphPlaying) return false;
    // Only while still near the top of the page / hero
    return (window.scrollY || 0) < Math.max(window.innerHeight * 0.35, 120);
  }

  onWheel(event) {
    // Finger / wheel scrolling "up" on the page (content moves up) = deltaY > 0
    if (event.deltaY > 0 && this.canTriggerMorph()) {
      this.playMorph();
    }
  }

  onTouchStart(event) {
    this.touchStartY = event.touches[0]?.clientY ?? null;
  }

  onTouchMove(event) {
    if (!this.canTriggerMorph() || this.touchStartY == null) return;
    const y = event.touches[0]?.clientY;
    if (y == null) return;
    // Finger moves up → page scrolls down → trigger morph
    if (this.touchStartY - y > 10) {
      this.playMorph();
    }
  }

  onScroll() {
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    this.rafId = window.requestAnimationFrame(() => {
      const y = window.scrollY || 0;
      const goingDown = y > this.lastY;

      // Back at the very top: allow the morph again
      if (y <= 2 && (this.morphDone || this.morphPlaying)) {
        this.resetBrand(true);
      } else if (goingDown && this.canTriggerMorph() && y > 2) {
        // Fallback if wheel/touch missed (trackpad / scrollbar)
        this.playMorph();
      }

      this.lastY = y;
    });
  }

  playMorph() {
    if (!this.brand || !this.morphEnabled || this.morphPlaying || this.morphDone) return;

    this.morphPlaying = true;
    const stageHeight = this.stage?.offsetHeight || window.innerHeight;
    const moveUp = stageHeight * 0.72;
    const scale = 0.12;

    // Ensure starting point, then animate in one go (not scrubbed by scroll)
    this.brand.classList.remove('is-morphed');
    this.brand.style.transition = 'none';
    this.brand.style.transform = 'translate3d(0, 0, 0) scale(1)';
    this.brand.style.opacity = '1';
    this.brand.style.visibility = 'visible';
    // Force reflow so the transition actually runs
    void this.brand.offsetWidth;

    this.brand.classList.add('is-morphing');
    this.brand.style.transition =
      'transform 0.95s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease 0.5s';

    requestAnimationFrame(() => {
      this.brand.style.transform = `translate3d(0, ${-moveUp}px, 0) scale(${scale})`;
      this.brand.style.opacity = '0';
      // Text appears in sync with brand disappearing
      this.showContent();
    });

    if (this.header?.setBrandArrived) this.header.setBrandArrived(true);
    else this.header?.classList.add('is-brand-arrived');

    const finish = () => {
      if (!this.morphPlaying) return;
      this.brand.classList.add('is-morphed');
      this.brand.classList.remove('is-morphing');
      this.morphPlaying = false;
      this.morphDone = true;
    };

    this.morphEndTimer = window.setTimeout(finish, 1100);
  }

  // Kept for compatibility if anything still calls progress morph
  updateMorph() {}
}

if (!customElements.get('luxury-hero')) {
  customElements.define('luxury-hero', LuxuryHero);
}
